/**
 * The activity groups an investment transaction is filed under. The web labels
 * and tones a row by its group and the API filters by it, so both read the one
 * mapping here.
 *
 * Every key equals its value: type-graphql exposes an enum's key names on the
 * wire, and the two must not drift apart.
 */
export const InvestmentActivity = {
  TRADE: 'TRADE',
  INCOME: 'INCOME',
  // One group for both directions: the amount's sign says which, and an asset
  // moved in arrives positive like a purchase.
  MONEY_MOVEMENT: 'MONEY_MOVEMENT',
  FEE: 'FEE',
  OTHER: 'OTHER',
} as const;

export type InvestmentActivity = (typeof InvestmentActivity)[keyof typeof InvestmentActivity];

export type ListedInvestmentActivity = Exclude<InvestmentActivity, typeof InvestmentActivity.OTHER>;

/** The provider's transaction type for a cancellation. A row of this type belongs to none of the listed groups. */
export const CANCEL_INVESTMENT_TRANSACTION_TYPE = 'cancel';

/**
 * The provider subtypes each group claims, spelled as stored. `OTHER` has no
 * list: it is every subtype absent from this table, plus every `cancel`-typed
 * row whatever its subtype.
 */
export const INVESTMENT_ACTIVITY_SUBTYPES: Record<ListedInvestmentActivity, readonly string[]> = {
  [InvestmentActivity.MONEY_MOVEMENT]: ['contribution', 'deposit', 'withdrawal', 'distribution', 'transfer', 'send'],
  [InvestmentActivity.INCOME]: [
    'dividend',
    'qualified dividend',
    'non-qualified dividend',
    'interest',
    'interest receivable',
    'long-term capital gain',
    'short-term capital gain',
    'unqualified gain',
  ],
  [InvestmentActivity.TRADE]: [
    'buy',
    'sell',
    'buy to cover',
    'sell short',
    'dividend reinvestment',
    'interest reinvestment',
    'long-term capital gain reinvestment',
    'short-term capital gain reinvestment',
    'trade',
  ],
  [InvestmentActivity.FEE]: [
    'account fee',
    'fund fee',
    'legal fee',
    'management fee',
    'margin expense',
    'miscellaneous fee',
    'transfer fee',
    'trust fee',
    'tax',
    'tax withheld',
    'non-resident tax',
  ],
};

const ACTIVITY_BY_SUBTYPE = new Map<string, ListedInvestmentActivity>(
  (Object.keys(INVESTMENT_ACTIVITY_SUBTYPES) as ListedInvestmentActivity[]).flatMap((activity) =>
    INVESTMENT_ACTIVITY_SUBTYPES[activity].map((subtype): [string, ListedInvestmentActivity] => [subtype, activity])
  )
);

/**
 * The one group a transaction belongs to. Matching is exact — no case or
 * whitespace folding — so a database predicate built from the same table
 * agrees with it row for row.
 */
export function resolveInvestmentActivity(type: string, subtype: string): InvestmentActivity {
  if (type === CANCEL_INVESTMENT_TRANSACTION_TYPE) {
    return InvestmentActivity.OTHER;
  }
  return ACTIVITY_BY_SUBTYPE.get(subtype) ?? InvestmentActivity.OTHER;
}

/** The kind of activity a transaction records: its subtype as stored, or `canceled` for a cancellation. */
export function resolveInvestmentTransactionKind(type: string, subtype: string): string {
  return type === CANCEL_INVESTMENT_TRANSACTION_TYPE ? 'canceled' : subtype;
}
